'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import parse from 'html-react-parser';
import { MenuItemType } from '@overlapdev/types';
import { ArrowLeftSVG, ArrowRightSVG, CloseSVG } from 'components/svg';
import MegaNavMobileItem from './item';

const MegaNavMobile = ({ item }: { item: MenuItemType }) => {
	//__Hooks
	const pathname = usePathname();

	//__Variables
	const { child_items, classes, id, link, target, title } = item || {};
	const no_link = classes?.includes('label') || false;
	const current_cat_path = `/${pathname.split('/')[1]}`;

	//__State
	const [currentId, setCurrentId] = useState(0);
	const [open, setOpen] = useState(false);
	const [offset, setOffset] = useState<number | string>(0);

	//__Function
	const openMega = (e: any, classes: string[]) => {
		e.preventDefault();
		document.body.classList.add('mega_open');
		if (classes.includes('primary')) {
			document.body.classList.add('primary_mega');
		} else if (classes.includes('secondary')) {
			document.body.classList.add('secondary_mega');
		} else {
			document.body.classList.add('neutral_mega');
		}
		setOpen(true);
	};

	const closeMega = () => {
		document.body.classList.remove('mega_open', 'primary_mega', 'secondary_mega', 'neutral_mega');
		setOpen(false);
	};

	const closeMobile = () => {
		document.body.classList.remove('mega_open', 'primary_mega', 'secondary_mega');
		document.documentElement.style.overflow = 'unset';
		document.body.classList.remove('mobile_nav_open');
		if (document.getElementsByClassName('mobile_nav') && document.getElementsByClassName('hamburger_nav')) {
			document.getElementsByClassName('mobile_nav')[0]?.classList?.remove('open');
			document.getElementsByClassName('hamburger_nav')[0]?.classList?.remove('open');
		}
		setOpen(false);
		setOffset(0);
		setCurrentId(0);
	};

	//__Use Effect
	useEffect(() => {
		setOpen(false);
		setOffset(0);
		setCurrentId(0);
	}, [pathname]);

	//__Callbacks
	const handleRoot = useCallback((offset: number | string, current_id: number) => {
		setOffset(offset);
		setCurrentId(current_id);
	}, []);

	return (
		<li key={`mega_nav_item-${id}`} className={`mega_nav_item ${classes.toString().replace(/,/g, ' ')}${pathname === link || current_cat_path === link ? ' current' : ''}`} id={id?.toString() || ''}>
			{no_link ? (
				<span className='cursor-pointer flex gap-3 items-center justify-between pr-0' onClick={(e) => openMega(e, classes)}>
					{parse(title || '')}
					<span className='cursor-pointer flex justify-center opacity-60 w-14' aria-label='toggle'>
						<ArrowRightSVG className='arrow' width={10} />
					</span>
				</span>
			) : (
				<Link className='flex gap-3 justify-between pr-0' href={link || ''} as={link} passHref={target === '_blank' ? true : false} target={target}>
					{parse(title || '')}
					<span className='block cursor-pointer flex justify-center opacity-60 relative w-14 z-10' aria-label='toggle' onClick={(e) => openMega(e, classes)}>
						<ArrowRightSVG className='arrow' width={10} />
					</span>
				</Link>
			)}

			<div className={`mega_wrapper absolute duration-300 ease-out h-full overflow-hidden text-black transition-all top-[48px] w-full z-90 ${open ? 'left-0' : 'left-full'}`}>
				<div className='flex h-full'>
					<div className={`mega_menu_mobile grow overflow-auto overflow-x-hidden`}>
						<div className='h-full relative transition-ease' style={{ left: offset }}>
							<div className='px-8 py-6'>
								<div className='cursor-pointer flex gap-3 items-center mb-6 mt-1.5' onClick={() => closeMega()} role='button'>
									<span>
										<ArrowLeftSVG className='opacity-60' width={7} />
									</span>
									<span className='font-semibold text-xl'>Back</span>
								</div>
								<div className='font-medium opacity-60 text-xs'>{parse(title)}</div>
							</div>
							<ul className='h-[calc(95dvh-170px)] overflow-auto'>
								{child_items.map((child_item) => (
									<MegaNavMobileItem key={`mega_menu-${child_item.id}`} item={child_item} level={0} main_title={item.title} handleRoot={handleRoot} current_id={currentId} root_id={parseInt(child_item.id.toString(), 10)} />
								))}
							</ul>
						</div>
					</div>
				</div>
				<div className='absolute bg-white cursor-pointer flex h-10 items-center justify-center right-6 rounded-full top-6 w-10' onClick={() => closeMobile()}>
					<CloseSVG width={14} />
				</div>
			</div>
		</li>
	);
};

export default MegaNavMobile;
